import React, { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Scale, 
  AlertCircle, 
  CheckCircle2, 
  CreditCard, 
  ShieldAlert, 
  Gavel, 
  MessageSquare
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const TermsAndConditionsPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "Security") return { ...link, route: "/security" };
        if (link.label === "Blog") return { ...link, route: "/blog" };
        if (link.label === "Help Center") return { ...link, route: "/help" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white text-left">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section className="curved-section shell-sky section-animate relative min-h-[40vh] flex items-center overflow-hidden pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="reveal">
              <span className="section-eyebrow">Legal Center</span>
              <h1 className="text-navy mt-6 mb-8">
                Terms of <span className="text-blue-600">Service.</span>
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                By using PMS, you agree to these terms. Please read them carefully.
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="shell-white py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16 reveal">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">1. Acceptance of Terms</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These Terms of Use constitute a legally binding agreement made between you, whether personally or on behalf of an entity (“you”) and Leanport Software Pvt. Ltd. (“Company”, “we”, “us”, or “our”), concerning your access to and use of the PMS website and software application.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
                    <Scale className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">2. Intellectual Property Rights</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Unless otherwise indicated, the Site and Application are our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics are owned or controlled by us.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">3. User Obligations</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  As a user of PMS, you agree that you will not:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>Systematically retrieve data or other content from the Site to create or compile a collection, database, or directory without written permission.</li>
                  <li>Circumvent, disable, or otherwise interfere with security-related features of the Site.</li>
                  <li>Engage in unauthorized framing of or linking to the Site.</li>
                  <li>Use the Site in a manner inconsistent with any applicable laws or regulations.</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">4. Billing and Payments</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We offer a flat-fee subscription model. All payments are non-refundable unless otherwise specified. We reserve the right to change our prices at any time upon providing notice to users.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center text-red-600">
                    <ShieldAlert className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">5. Limitation of Liability</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages arising from your use of the site or application.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center text-sky">
                    <Gavel className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-navy">6. Governing Law</h2>
                </div>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These Terms shall be governed by and defined following the laws of India. Leanport Software Pvt. Ltd. and yourself irrevocably consent that the courts of Noida, Uttar Pradesh shall have exclusive jurisdiction to resolve any dispute which may arise.
                </p>
              </div>

              <div className="p-10 bg-gray-900 rounded-[40px] text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Have questions about our terms?</h3>
                <p className="text-blue-100 mb-8">Our legal team is available to help clarify any of our service agreements.</p>
                <Link to="/contact" className="btn-secondary !bg-sky !text-navy !border-sky">
                  Contact Legal Support
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default TermsAndConditionsPage;
